import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import CollaboratorRow from './CollaboratorRow';
import AllCollaboratorsSheet from './AllCollaboratorsSheet';
import CollaboratorsFacultyChart from './CollaboratorsFacultyChart';
import ViewToggleButton from './ViewToggleButton';
import type { Collaborator } from '../../types/api';

const TOP_COUNT = 10;
const SKELETON_ROWS = Array.from({ length: 6 });
type CollaboratorsView = 'list' | 'chart';

interface CollaboratorsListProps {
  collaborators: Collaborator[];
  loading: boolean;
  error: string | null;
  onPersonClick: (personId: number, e: React.MouseEvent) => void;
  onProjectClick: (projectId: number, e: React.MouseEvent) => void;
  onShowGraph: () => void;
  graphLoading: boolean;
}

export default function CollaboratorsList({
  collaborators,
  loading,
  error,
  onPersonClick,
  onProjectClick,
  onShowGraph,
  graphLoading,
}: CollaboratorsListProps) {
  const { t } = useTranslation('person');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [view, setView] = useState<CollaboratorsView>('list');

  const top = collaborators.slice(0, TOP_COUNT);
  const hasMore = collaborators.length > TOP_COUNT;
  const hasData = !loading && !error && collaborators.length > 0;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
            {t('sections.collaborators')}
          </span>
          <span className="text-[11px] font-mono tabular-nums text-ctu-text-muted">
            {collaborators.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {hasData && (
            <>
              <ViewToggleButton active={view === 'chart'} onClick={() => setView('chart')}>
                {t('buttons.viewChart')}
              </ViewToggleButton>
              <span className="text-ctu-text-muted/40 text-xs select-none">/</span>
              <ViewToggleButton active={view === 'list'} onClick={() => setView('list')}>
                {t('buttons.viewList')}
              </ViewToggleButton>
            </>
          )}
          {hasMore && (
            <>
              <span className="mx-2 h-4 w-px bg-border" aria-hidden />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs font-medium text-ctu-text-muted hover:text-ctu-text"
                onClick={() => setSheetOpen(true)}
              >
                {t('buttons.viewAllCollaborators')} ›
              </Button>
            </>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex flex-col gap-2">
          {SKELETON_ROWS.map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {!loading && !error && top.length === 0 && (
        <p className="text-sm text-ctu-text-muted">
          {t('collaboratorsSection.noCollaborators')}
        </p>
      )}

      {hasData && view === 'list' && (
        <ol className="m-0 flex flex-col divide-y divide-border list-none p-0">
          {top.map((c, idx) => (
            <CollaboratorRow
              key={c.id}
              collaborator={c}
              index={idx}
              showIndex
              onPersonClick={onPersonClick}
              onProjectClick={onProjectClick}
            />
          ))}
        </ol>
      )}

      {hasData && view === 'chart' && (
        <div className="flex flex-1 items-center justify-center">
          <CollaboratorsFacultyChart collaborators={collaborators} />
        </div>
      )}

      {hasData && (
        <div className="mt-4 pt-3 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs font-medium text-ctu-text-muted hover:text-ctu-text"
            onClick={onShowGraph}
            disabled={graphLoading}
          >
            {graphLoading
              ? t('collaborationGraph.loading')
              : `${t('buttons.showCollaborationGraph')} ›`}
          </Button>
        </div>
      )}

      <AllCollaboratorsSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        collaborators={collaborators}
        onPersonClick={onPersonClick}
        onProjectClick={onProjectClick}
      />
    </div>
  );
}
